import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { L10N_LOCALE, L10nLocale, L10nTranslationModule } from 'angular-l10n';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { CountryModel } from '../../../core/models/countries/country.model';
import { CountryService } from '../../../core/services/other/country.service';
import { SupplierContactFormModel } from '../../../core/models/supplierDB/supplierContactForm.model';
import { AppConstants } from '../../../app.constants';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'supplierDB-registration-registration-contact',
  standalone: true,
  imports: [
    FileUploadModule,
    DividerModule,
    ImageModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    L10nTranslationModule,
    FileUploadModule,
    TooltipModule,
    CheckboxModule,
    InputNumberModule
  ],
  templateUrl: './registration-contact.component.html',
  styleUrl: './registration-contact.component.scss',
})
export class RegistrationContactComponent implements OnInit, OnDestroy {
  @Output() logoData = new EventEmitter<File>();

  // create form
  public supplierForm!: FormGroup;

  // selection data
  public countryData: CountryModel[] = [];

  public logo: File[] = [];
  public logoSrc: string | null = null;
  public defaultLogoSrc: string = AppConstants.CARRIERDB_DEFAULT_LOGO_SOURCE;

  private _valueChangeSubscription!: Subscription;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private _formBuilder: FormBuilder,
    private _countryService: CountryService
  ) {
    this._initFormControls();
    this._initCountryData();
  }

  ngOnInit() {
    var supplierContactInfo = localStorage.getItem(
      AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_CONTACT
    );
    if (supplierContactInfo !== null) {
      this.supplierForm.patchValue(JSON.parse(supplierContactInfo));
      if (this.supplierForm.value.coverADR === null) {
        this.supplierForm.patchValue({ "coverADR": false });
      }
    }

    this._loadLogo();

    this._valueChangeSubscription = this.supplierForm.valueChanges.subscribe(
      (_) => {
        const values: SupplierContactFormModel = this.supplierForm.getRawValue();
        localStorage.setItem(
          AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_CONTACT,
          JSON.stringify(values)
        );
      }
    );

  }

  ngOnDestroy(): void {
    this._valueChangeSubscription.unsubscribe();
  }

  private _initCountryData(): void {
    this.countryData = this._countryService.getCountries();
  }

  private _initFormControls(): void {
    this.supplierForm = this._formBuilder.group({
      companyName: [{ value: null, disabled: true }, [Validators.required, Validators.minLength(2)]],
      companyLogo: [null],
      representativeFirstName: [{ value: null, disabled: true }, [Validators.required, Validators.minLength(2)]],
      representativeLastName: [{ value: null, disabled: true }, [Validators.required, Validators.minLength(2)]],
      phoneNumber: [null, [Validators.required, Validators.pattern(AppConstants.PHONE_NUMBER_WEBSITE_VALIDATION_REGEX)]],
      mobileNumber: [null, [Validators.pattern(AppConstants.PHONE_NUMBER_WEBSITE_VALIDATION_REGEX)]],
      emailAddress: [{ value: null, disabled: true }, [Validators.required, Validators.email]],
      website: [null, [Validators.pattern(AppConstants.WEBSITE_VALIDATION_REGEX)]],
      street: [null, [Validators.required, Validators.minLength(2)]],
      postalCode: [null, [Validators.required, Validators.pattern(AppConstants.POSTAL_CODE_VALIDATION_REGEX)]],
      city: [null, [Validators.required, Validators.minLength(2)]],
      country: [null, [Validators.required]],
      co2Score: [null, [Validators.pattern(AppConstants.NUMBER_REGEX)]],
      co2EmissionTotal: [null, [Validators.pattern(AppConstants.NUMBER_REGEX)]],
      scorePlatform: [null, [Validators.minLength(2)]],
      scorePlatformLink: [null, [Validators.pattern(AppConstants.WEBSITE_VALIDATION_REGEX)]],
      coverADR: [false],
    });
  }

  /**
   * Loads the logo from the provided base64 string and converts it into a File object.
   * This function is used to populate the `logo` property with a File object when the `logoSrc` property is set.
   * @returns {void}
   */
  private _loadLogo() {
    this.logoSrc = localStorage.getItem(
      AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_LOGO
    );
    if (this.logoSrc == null) return;
    const base64 = this.logoSrc.split(',');
    const byteCharacters = atob(base64[1]);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });
    this.logo = [new File([blob], 'image.png', { type: 'image/png' })];
    this._setLogo(this.logo[0]);
  }

  /**
   * Sets the logo from the provided File object.
   * @param {File} file - The File object containing the logo image.
   * @returns {void}
   */
  private _setLogo(file: File) {
    this.logo = [file];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.logoSrc = reader.result!.toString();
      localStorage.setItem(
        AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_LOGO,
        reader.result as string
      );
    };
  }

  /**
   * set file when file is selected
   * @param {FileSelectEvent} event
   */
  public onSelect(event: FileSelectEvent) {
    this._setLogo(event.files[0]);
  }

  /**
   * Resets the logo to the default image and clears the logo selection.
   * @returns {void}
   */
  public onLogoReset(): void {
    this.logo = [];
    this.logoSrc = AppConstants.CARRIERDB_DEFAULT_LOGO_SOURCE;
    this.supplierForm.patchValue({ companyLogo: null });
    localStorage.removeItem(AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_LOGO);
  }

  /**
   * Check if the form is valid
   * @returns {boolean} - true if the form is valid
   */
  public isFormValid(): boolean {
    return this.supplierForm.valid;
  }
}
